import { uniq } from "lodash";
import mixpanel, { type Dict } from "mixpanel-browser";

import { getEnv, getServiceTag } from "../../../../commons/libs/env-service";
import { createPromiseQueue } from "../../../../commons/libs/promise";
import { getSettings } from "../../../../commons/libs/settings-service";
import type { ActiveFilters, FilterKey } from "../../../../commons/specs/filters";
import { ProductSpecKey, type Product, type ProductId, type ProductType } from "../../../../commons/specs/product";
import type { Slide } from "../../../../commons/types/customer-world";
import type { PrintType } from "../../../../commons/types/print-mail";
import type { MixpanelInitConfig } from "../../config/config-types";
import { getProductProperties } from "../tracking-product-properties";

import type { Sentry } from "./sentry";

const session: {
  active: boolean;
  idle: boolean;
  started: number;
  interactionCount: number;
  timeout: NodeJS.Timeout | undefined;
} = {
  active: false,
  idle: false,
  started: 0,
  interactionCount: 0,
  timeout: undefined
};

export enum TrackingEvent {
  AppStarted = "App started",
  PageViewed = "Page viewed",
  BookmarkCreated = "Bookmark created",
  BookmarkRenamed = "Bookmark renamed",
  BookmarkRemoved = "Bookmark removed",
  BookmarkCleared = "Bookmark cleared",
  AddedToBookmark = "Added to bookmark",
  RemovedFromBookmark = "Removed from bookmark",
  Printed = "Printed",
  ProductPrinted = "Product printed",
  PrintFailed = "Print failed",
  Mailed = "Mailed",
  ProductMailed = "Product mailed",
  MailFailed = "Mail failed",
  ErrorSet = "Error set",
  SessionStarted = "Session started",
  SessionEnded = "Session ended",
  SessionIdled = "Session idled",
  ToolStarted = "Tool started",
  ToolEnded = "Tool ended",
  BrowserPageViewed = "Browser page viewed",
  ToolEvent = "Tool event [%toolName%]: %eventName%",
  ProductsCompared = "Products compared",
  FilterModalOpened = "Filter modal opened",
  FilterModalClosed = "Filter modal closed",
  FilterModalChanged = "Filter changed",
  FilterCleared = "Filter cleared",
  ProductViewed = "Product viewed",
  ProductAvailabilityViewed = "Product availability viewed",
  CustomerWorldViaTimeout = "CustomerWorld started via timeout",
  CustomerWorldStarted = "CustomerWorld started",
  CustomerWorldEnded = "CustomerWorld ended",
  CustomerWorldEndedBy = "CustomerWorld ended by",
  CustomerWorldSlideInteracted = "CustomerWorld slide interacted",
  SlideStarted = "Slide started",
  SlideEnded = "Slide ended",
  VideoConferenceStarted = "VideoConference started",
  VideoConferenceEnded = "VideoConference ended",
  ProductConfiguratorOpened = "Product configurator opened",
  ModalOpened = "Modal opened",
  ModalClosed = "Modal closed",
  AlteosAdvertisementClicked = "Alteos advertisement clicked",
  ZegPlusGarantieAdvertisementClicked = "ZegPlusGarantie advertisement clicked",
  ProductDetailsNavigated = "Product details navigated",
  ProductDetailsImageZoomOpened = "Product details image zoom opened",
  ProductDetailsImageZoomClosed = "Product details image zoom closed",
  ProductSpecificationsOpened = "Product specifications opened",
  ProductSpecificationsClosed = "Product specifications closed",
  ProductVariantsOpened = "Product variants opened",
  ProductVariantsClosed = "Product variants closed",
  BrandWorldClickedOnProductDetails = "BrandWorld clicked on product details",
  AvailabilityVisibilityFallback = "Availability visibility fallback applied",
  AvailabilityFilterTeaserOpened = "Availability filter teaser opened",
  AvailabilityFilterTeaserCtaClicked = "Availability filter teaser CTA clicked",
  AvailabilityFilterTeaserDismissed = "Availability filter teaser dismissed"
}

interface PrintContext {
  type: PrintType;
  numCopies: string;
  initiator?: string;
}

interface MailContext {
  type: PrintType;
  initiator?: string;
}

const convertSlideToMixpanelParams = (slide: Slide) => ({
  slideType: slide.type,
  id: slide.id,
  package: slide.package,
  associatedTools: slide.associated ? slide.associated.tools : undefined,
  associatedBrands: slide.associated ? slide.associated.brands : undefined
});

export interface MixpanelConfig {
  appVersion: string;
  sentry: Sentry;
  config: MixpanelInitConfig;
}

const initMixpanel = ({ appVersion, sentry, config }: MixpanelConfig) => {
  const onInteraction = () => {
    session.interactionCount += 1;
    beginOrRefreshSession();
  };

  const trackingQueue = createPromiseQueue({ autoStart: false });

  const enqueueTracking = (task: () => void): void => {
    void trackingQueue.add(task).catch((error: unknown) => {
      sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    });
  };

  /**
   * Initializes mixpanel and identifies the user.
   *
   * @param token
   * @param options Optional Mixpanel init options.
   * @param options.debug Enables Mixpanel debug mode.
   * @param options.apiHost Overrides the Mixpanel ingestion API host.
   */
  const init = async (token: string, options: { debug?: boolean; apiHost?: string } = {}) => {
    const { debug = false, apiHost } = options;

    mixpanel.init(token, {
      disable_cookie: true,
      debug,
      ...(apiHost ? { api_host: apiHost } : {})
    });
    trackAppStarted();

    (window as Window).addEventListener("mousedown", onInteraction);
    (window as Window).addEventListener("touchstart", onInteraction);

    try {
      const serviceTag = await getServiceTag();

      if (serviceTag) {
        mixpanel.identify(serviceTag);
        trackingQueue.start();
      }
    } catch (error) {
      console.error("Couldn't configure serviceTag as Mixpanel unique_id.", error);
      sentry.captureException(new Error("Couldn't configure serviceTag as Mixpanel unique_id."));
    }
  };

  const refreshSession = () => {
    if (!session.active) {
      return;
    }

    if (session.timeout) {
      clearTimeout(session.timeout);
    }

    session.timeout = setTimeout(() => endSession(true), config.sessionTimeout);
  };

  const beginSession = () => {
    if (session.active) {
      return;
    }

    session.active = true;
    session.started = new Date().getTime();

    track(TrackingEvent.SessionStarted);
    timeEvent(TrackingEvent.SessionEnded); // To track session duration

    if (session.idle) {
      track(TrackingEvent.SessionIdled);
      session.idle = false;
    }

    // Init session timeout
    refreshSession();
  };

  const beginOrRefreshSession = () => {
    if (session.active) {
      refreshSession();
    } else {
      beginSession();
    }
  };

  const endSession = (timeout: boolean = false) => {
    if (!session.active) {
      return;
    }

    track(TrackingEvent.SessionEnded, { timeout, interactionCount: session.interactionCount });
    timeEvent(TrackingEvent.SessionIdled); // To track idle time with next session start event

    clearTimeout(session.timeout);

    session.timeout = undefined;
    session.started = 0;
    session.interactionCount = 0;
    session.idle = true;
    session.active = false;
  };

  /**
   * Registers super properties that are sent with every Mixpanel event.
   * Called on app init. Variant-specific user profile data is set via `setUserProfile`.
   */
  const registerUserProfile = async () => {
    const env = await getEnv();
    const settings = await getSettings();

    mixpanel.register({
      serviceTag: env.serviceTag,
      appVersion,
      deviceType: env.deviceType,
      customerGroup: env.customerGroup,
      currency: settings.currency
    });
  };

  const track = (event: TrackingEvent, properties?: Dict) => {
    enqueueTracking(() => mixpanel.track(event, properties));
  };

  const timeEvent = (event: string) => {
    enqueueTracking(() => mixpanel.time_event(event));
  };

  const trackAppStarted = () => {
    track(TrackingEvent.AppStarted);
  };

  const trackPageViewed = (url: string, path: string, params: { [param: string]: string } = {}) => {
    track(TrackingEvent.PageViewed, { url, path, params });
  };

  const trackPrinted = (context: PrintContext) => {
    track(TrackingEvent.Printed, context);
  };

  const trackProductPrinted = (context: PrintContext, product: Product, variantId?: ProductId) => {
    track(TrackingEvent.ProductPrinted, { ...context, ...getProductProperties(product, { variantId }) });
  };

  const trackPrintFailed = () => {
    track(TrackingEvent.PrintFailed);
  };

  const trackProductMailed = (context: MailContext, product: Product, variantId?: ProductId) => {
    track(TrackingEvent.ProductMailed, { ...context, ...getProductProperties(product, { variantId }) });
  };

  const trackBrandWorldClickedOnProductDetails = (brandWorldName: string, product: Product) => {
    track(TrackingEvent.BrandWorldClickedOnProductDetails, {
      brandWorldName,
      ...getProductProperties(product)
    });
  };

  const trackMailed = (context: MailContext) => {
    track(TrackingEvent.Mailed, context);
  };

  const trackMailFailed = () => {
    track(TrackingEvent.MailFailed);
  };

  const trackProductsCompared = (products: Product[]) => {
    const enrichedProducts = products.map(product => getProductProperties(product));
    const brandKeys = uniq(enrichedProducts.map(({ brandKey }) => brandKey));
    const brandIds = uniq(enrichedProducts.map(({ brandId }) => brandId));

    track(TrackingEvent.ProductsCompared, {
      numProducts: enrichedProducts.length,
      numBrands: brandKeys.length,
      brandKeys,
      brandIds,
      products: enrichedProducts
    });
  };

  const trackFilterModalOpened = (type: string) => {
    track(TrackingEvent.FilterModalOpened, { type });
  };

  const trackFilterModalClosed = (type: string) => {
    track(TrackingEvent.FilterModalClosed, { type });
  };

  const trackFilterCleared = (type: string) => {
    track(TrackingEvent.FilterCleared, { type });
  };

  const trackFilterChanged = <Type extends ProductType>(filters: ActiveFilters<Type>, searchTerm: string) => {
    const filterParams = Object.keys(filters ?? {}).reduce(
      (aggr, key) => ({ ...aggr, [`filter.${key}`]: filters?.[key as FilterKey<Type>] }),
      {}
    );

    track(TrackingEvent.FilterModalChanged, {
      ...filterParams,
      searchTerm,
      filters: Object.keys(filters ?? {})
    });
  };

  const trackProductViewed = (product: Product): void => {
    track(TrackingEvent.ProductViewed, {
      ...getProductProperties(product)
    });
  };

  const trackProductAvailabilityViewed = (
    product: Product,
    isWawiActive: boolean,
    isVeloconnectActive: boolean,
    variantId?: ProductId
  ): void => {
    track(TrackingEvent.ProductAvailabilityViewed, {
      isWawiActive,
      isVeloconnectActive,
      ...getProductProperties(product, { variantId })
    });
  };

  const trackProductConfiguratorOpened = (product: Product): void => {
    track(TrackingEvent.ProductConfiguratorOpened, {
      ...getProductProperties(product),
      configuratorUrl: product[ProductSpecKey.ConfiguratorUrl]
    });
  };

  const trackToolStarted = (toolName: string, entryFile?: string) => {
    track(TrackingEvent.ToolStarted, { toolName, ...(entryFile ? { entryFile } : {}) });
    timeEvent(TrackingEvent.ToolEnded); // To track tool duration
  };

  const trackToolEnded = (toolName: string, entryFile?: string) => {
    track(TrackingEvent.ToolEnded, { toolName, ...(entryFile ? { entryFile } : {}) });
  };

  const trackSlideStarted = (slide: Slide) => {
    if (!slide.analytics) {
      return;
    }

    track(TrackingEvent.SlideStarted, convertSlideToMixpanelParams(slide));
    timeEvent(TrackingEvent.SlideEnded); // To track slide duration
  };

  const trackSlideEnded = (slide: Slide) => {
    if (!slide.analytics) {
      return;
    }

    track(TrackingEvent.SlideEnded, convertSlideToMixpanelParams(slide));
  };

  const trackToolEvent = (toolName: string, eventName: string, properties: Record<string, unknown>) => {
    const event = TrackingEvent.ToolEvent.replace("%toolName%", toolName).replace("%eventName%", eventName);
    enqueueTracking(() => mixpanel.track(event, { ...properties, toolName }));
  };

  const trackBrowserPageViewed = (url: string, toolName?: string) => {
    track(TrackingEvent.BrowserPageViewed, { url, toolName });
  };

  const trackCustomerWorldViaTimeout = () => {
    track(TrackingEvent.CustomerWorldViaTimeout);
  };

  const trackCustomerWorldStarted = () => {
    track(TrackingEvent.CustomerWorldStarted);
    timeEvent(TrackingEvent.CustomerWorldEnded); // To track customer world duration
  };

  const trackCustomerWorldEnded = () => {
    track(TrackingEvent.CustomerWorldEnded);
  };

  const trackCustomerWorldEndedBy = (endedBy: "Call To Action" | "Home Button") => {
    track(TrackingEvent.CustomerWorldEndedBy, { endedBy });
  };

  const trackCustomerWorldSlideInteracted = (slide: Slide) => {
    track(TrackingEvent.CustomerWorldSlideInteracted, convertSlideToMixpanelParams(slide));
  };

  const trackModalOpened = (contentType: string) => {
    track(TrackingEvent.ModalOpened, { contentType });
    timeEvent(TrackingEvent.ModalClosed); // To track session duration
  };

  const trackModalClosed = () => {
    track(TrackingEvent.ModalClosed);
  };

  const trackVideoConferenceStarted = () => {
    track(TrackingEvent.VideoConferenceStarted);
    timeEvent(TrackingEvent.VideoConferenceEnded); // To track duration
  };

  const trackVideoConferenceEnded = () => {
    track(TrackingEvent.VideoConferenceEnded);
  };

  const trackAlteosAdvertisementClicked = (shopUrl: string, product: Product) => {
    track(TrackingEvent.AlteosAdvertisementClicked, { shopUrl, ...getProductProperties(product) });
  };

  const trackZegPlusGarantieAdvertisementClicked = (product: Product) => {
    track(TrackingEvent.ZegPlusGarantieAdvertisementClicked, { ...getProductProperties(product) });
  };

  const trackProductDetailsNavigated = (
    interaction: "click" | "swipe",
    direction: "next" | "prev",
    fromProduct: {
      productId: ProductId;
      brandKey: string;
      modelName: string;
    },
    toProductId: ProductId
  ) => {
    track(TrackingEvent.ProductDetailsNavigated, { interaction, direction, fromProduct, toProductId });
  };

  const trackProductDetailsImageZoomOpened = (product: Product, variantId?: ProductId) => {
    track(TrackingEvent.ProductDetailsImageZoomOpened, getProductProperties(product, { variantId }));
    timeEvent(TrackingEvent.ProductDetailsImageZoomClosed); // To track duration
  };

  const trackProductDetailsImageZoomClosed = (product: Product, variantId?: ProductId) => {
    track(TrackingEvent.ProductDetailsImageZoomClosed, getProductProperties(product, { variantId }));
  };

  const trackProductSpecificationsOpened = (product: Product): void => {
    track(TrackingEvent.ProductSpecificationsOpened, getProductProperties(product));
    timeEvent(TrackingEvent.ProductSpecificationsClosed); // To track duration
  };

  const trackProductSpecificationsClosed = (product: Product): void => {
    track(TrackingEvent.ProductSpecificationsClosed, getProductProperties(product));
  };

  const trackProductVariantsOpened = (product: Product): void => {
    track(TrackingEvent.ProductVariantsOpened, getProductProperties(product));
    timeEvent(TrackingEvent.ProductVariantsClosed); // To track duration
  };

  const trackProductVariantsClosed = (product: Product): void => {
    track(TrackingEvent.ProductVariantsClosed, getProductProperties(product));
  };

  const trackAvailabilityVisibilityFallback = (
    configuredSetting: string,
    appliedSetting: string,
    hasWawiData: boolean,
    hasVeloconnectData: boolean
  ): void => {
    track(TrackingEvent.AvailabilityVisibilityFallback, {
      configuredSetting,
      appliedSetting,
      hasWawiData,
      hasVeloconnectData
    });
  };

  const trackAvailabilityFilterTeaserOpened = (): void => {
    track(TrackingEvent.AvailabilityFilterTeaserOpened);
  };

  const trackAvailabilityFilterTeaserCtaClicked = (): void => {
    track(TrackingEvent.AvailabilityFilterTeaserCtaClicked);
  };

  const trackAvailabilityFilterTeaserDismissed = (): void => {
    track(TrackingEvent.AvailabilityFilterTeaserDismissed);
  };

  return {
    init,
    registerUserProfile,
    refreshSession,
    beginSession,
    beginOrRefreshSession,
    endSession,
    track,
    trackAppStarted,
    trackPageViewed,
    trackPrinted,
    trackProductPrinted,
    trackPrintFailed,
    trackMailed,
    trackProductMailed,
    trackMailFailed,
    trackProductsCompared,
    trackFilterModalOpened,
    trackFilterModalClosed,
    trackFilterCleared,
    trackFilterChanged,
    trackProductViewed,
    trackProductAvailabilityViewed,
    trackProductConfiguratorOpened,
    trackToolStarted,
    trackToolEnded,
    trackSlideStarted,
    trackSlideEnded,
    trackToolEvent,
    trackBrowserPageViewed,
    trackCustomerWorldViaTimeout,
    trackCustomerWorldStarted,
    trackCustomerWorldSlideInteracted,
    trackCustomerWorldEnded,
    trackCustomerWorldEndedBy,
    trackModalOpened,
    trackModalClosed,
    trackVideoConferenceStarted,
    trackVideoConferenceEnded,
    trackAlteosAdvertisementClicked,
    trackZegPlusGarantieAdvertisementClicked,
    trackProductDetailsNavigated,
    trackProductDetailsImageZoomOpened,
    trackProductDetailsImageZoomClosed,
    trackProductSpecificationsOpened,
    trackProductSpecificationsClosed,
    trackProductVariantsOpened,
    trackProductVariantsClosed,
    trackBrandWorldClickedOnProductDetails,
    trackAvailabilityVisibilityFallback,
    trackAvailabilityFilterTeaserOpened,
    trackAvailabilityFilterTeaserCtaClicked,
    trackAvailabilityFilterTeaserDismissed
  };
};

export default initMixpanel;
